<script setup lang="ts">
import type { CommandPaletteGroup, CommandPaletteItem } from '@nuxt/ui';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from '#app/composables/router';
import { useStore } from '#imports';
import SpaceOrPageSelector from '#layers/spaces/components/SpaceOrPageSelector.vue';
import { useCurrentPageNotRendered } from '#layers/spaces/composables/use-current-page-not-rendered';
import { useCurrentSpace } from '#layers/spaces/composables/use-current-space';
import { useSpaceEdit } from '#layers/spaces/composables/use-space-edit';
import { useSpaces } from '#layers/spaces/composables/use-spaces';
import { ASSETS_ROUTE_NAME, CONTENT_ROUTE_NAME } from '#layers/spaces/constants/workspace-routes';
import { translateSpace } from '#layers/spaces/utils/space';
import { useWorkspaceStore } from '#layers/workspace/public';

interface WorkspaceNavigationItem {
	id: string;
	title: string;
	icon: string;
	routeName: string;
	pathSegment: string;
}

const i18n = useI18n();
const store = useStore();
const route = useRoute();

const { openSpaceEdit, createSpace, deleteSpace } = useSpaceEdit();
const { currentWorkspaceSlug } = await useWorkspaceStore();
const { currentSpaceTranslated } = useCurrentSpace();
const { currentPageNotRendered } = useCurrentPageNotRendered();

const spaces = useSpaces();

const workspaceRouteItems = computed<WorkspaceNavigationItem[]>(() => [
	{
		id: CONTENT_ROUTE_NAME,
		title: i18n.t('pages.content.title'),
		icon: 'lucide:box',
		routeName: CONTENT_ROUTE_NAME,
		pathSegment: 'content',
	},
	{
		id: ASSETS_ROUTE_NAME,
		title: i18n.t('common.assets'),
		icon: 'lucide:folder',
		routeName: ASSETS_ROUTE_NAME,
		pathSegment: 'assets',
	},
]);

const currentWorkspaceRouteItem = computed(() =>
	workspaceRouteItems.value.find((item) =>
		isCurrentWorkspaceRoute(item),
	),
);

const currentSpace = computed(() =>
	currentWorkspaceRouteItem.value
		? undefined
		: currentSpaceTranslated.value
			?? currentPageNotRendered.value?.space
			?? (route.params.spaceId ? store.MonospaceSpace.peekFirst(route.params.spaceId as string) : undefined),
);

const canEditSpace = computed(() => !currentWorkspaceRouteItem.value && !!currentSpace.value);
const currentTitle = computed(() => currentWorkspaceRouteItem.value?.title);

const workspaceNavigationGroups = computed<CommandPaletteGroup<CommandPaletteItem>[]>(() => [
	{
		id: 'workspace-navigation',
		label: i18n.t('workspace.breadcrumb.workspace'),
		items: workspaceRouteItems.value.map((item) => ({
			label: item.title,
			icon: item.icon,
			active: currentWorkspaceRouteItem.value?.id === item.id,
			to: {
				name: item.routeName,
				params: { workspace: currentWorkspaceSlug.value },
			},
		})),
	},
]);

async function handleDeleteSpace() {
	if (!currentSpace.value) return;

	const space = await store.MonospaceSpace.findFirst(currentSpace.value.id);
	if (space) deleteSpace(space);
}

function isCurrentWorkspaceRoute(item: WorkspaceNavigationItem) {
	if (typeof route.name === 'string' && (route.name === item.routeName || route.name.startsWith(`${item.routeName}.`))) {
		return true;
	}

	return route.path.includes(`/system/${item.pathSegment}`);
}
</script>

<template>
	<SpaceOrPageSelector
		:items="spaces"
		:current-item="currentSpace"
		:current-title
		:groups-before="workspaceNavigationGroups"
		:can-edit="canEditSpace"
		can-create
		:item-props="{
			getId: (space) => space?.id ?? '',
			getTitle: (space) => space ? translateSpace(space, i18n).title : '',
			getIcon: (space) => space?.icon || 'lucide:file',
			getRoute: (space) => ({
				name: space?.id,
				params: { workspace: currentWorkspaceSlug },
			}),
		}"
		:labels="{
			create: i18n.t('space.new_space'),
			delete: i18n.t('space.delete_space_label'),
			edit: i18n.t('space.edit_space'),
			group: i18n.t('workspace.breadcrumb.spaces'),
		}"
		:tooltips="{
			create: i18n.t('space.cannot_create'),
			delete: i18n.t('space.cannot_delete'),
			edit: i18n.t('space.cannot_edit'),
		}"
		:placeholder="i18n.t('common.search')"
		:content="{ align: 'start' }"
		@edit="openSpaceEdit"
		@create="createSpace"
		@delete="handleDeleteSpace"
	>
		<template #default="{ open, currentTitle: selectorTitle }">
			<DButton
				color="neutral"
				variant="ghost"
				:pressed="open"
				class="min-w-0 text-sm block-7"
				:ui="{
					base: 'flex items-center gap-2 text-left font-semibold',
					trailingIcon: 'size-4 flex-none shrink-0',
				}"
			>
				<span class="min-w-0 flex-1 truncate">
					{{ selectorTitle }}
				</span>
			</DButton>
		</template>
	</SpaceOrPageSelector>
</template>
